import React, {
    CSSProperties,
    ImgHTMLAttributes,
    RefObject,
    useEffect,
    useRef,
    useState,
} from 'react';

import clsx from 'clsx';

import { isValueSet } from '@utils/guards';
import {
    defaultMediaQueries,
    extractMobileImages,
    extractWebImages,
    getFirstURL,
    getPreviewSRC,
    getSRC,
    Image,
    imageFormatsInOrder,
    ImageRatios,
    MobileImageSizes,
    sortImageFormats,
    WebImageSizes,
} from '@utils/picture';
import { getMinWidthMedia, getSafeURL } from '@utils/utils';

import styles from './Picture.module.scss';

import { NoImageSquareIcon } from '../../icons';
import { Spinner } from '../Spinner/Spinner';

type ClassKey = 'picture' | 'preview';
type PictureProps = {
    src?: string;
    src2x?: string;
    alt?: string;
    lazy?: boolean;
    isWithLoader?: boolean;
    sources?: Global.ImageSet | null;
    imageRef?: RefObject<HTMLImageElement>;
    mediaQueries?: Record<WebImageSizes, number>;
    classes?: Classes<ClassKey>;
    onLoad?: () => void;
    shouldHaveItemProp?: boolean;
    priority?: boolean;
} & ImgHTMLAttributes<HTMLImageElement>;

type ImageDimensions = Pick<ImgHTMLAttributes<HTMLImageElement>, 'width' | 'height'>;

const getImageDimensions = (image: Image | undefined): ImageDimensions =>
    image !== undefined && image.width > 0 && image.height > 0
        ? { width: image.width, height: image.height }
        : {};

const Picture: React.FunctionComponent<PictureProps> = (props) => {
    const {
        src,
        src2x,
        alt,
        sources,
        onLoad,
        imageRef,
        classes,
        isWithLoader = false,
        lazy = true,
        mediaQueries = defaultMediaQueries,
        shouldHaveItemProp = false,
        priority = false,
        ...otherProps
    } = props;

    const isLazy = priority ? false : lazy;
    const priorityAttrs: Record<string, string> = priority ? { fetchpriority: 'high' } : {};

    const [isImageLoaded, setIsImageLoaded] = useState(false);

    const pictureRef = useRef<HTMLPictureElement>(null);
    const defaultImageRef = useRef<HTMLImageElement>(null);
    const currentImageRef = imageRef || defaultImageRef;

    const onLoadImage = (): void => setIsImageLoaded(true);

    useEffect(() => {
        if (
            isLazy &&
            currentImageRef &&
            currentImageRef.current !== null &&
            currentImageRef.current.complete
        ) {
            onLoadImage();
        }
    }, [currentImageRef, isLazy]);

    if (isLazy) otherProps.loading = 'lazy';

    if (!isValueSet(src) && !isValueSet(sources)) {
        return (
            <div className={clsx(classes?.picture, styles.noImageWrapper)}>
                <NoImageSquareIcon className={styles.noImageIcon} />
            </div>
        );
    }

    if (sources) {
        if (sources.web !== undefined) {
            const webSources = sources.web;

            const renderSources = (size: WebImageSizes, index: number): JSX.Element => (
                <React.Fragment key={index}>
                    {extractWebImages(webSources, size, 'x1')
                        .sort(sortImageFormats(imageFormatsInOrder))
                        .flatMap((image, imageIndex) => {
                            const images2x = extractWebImages(webSources, size, 'x2').flatMap(
                                (image2x) => image2x,
                            );
                            const image2x = images2x.find(
                                ({ urlAndFormat }) =>
                                    urlAndFormat.format === image.urlAndFormat.format,
                            );

                            const src1x = getSRC(image.urlAndFormat.url);
                            const x1 = getSafeURL(src1x);
                            const x2URL = image2x?.urlAndFormat.url;
                            const x2 = x2URL ? getSafeURL(getSRC(x2URL)) : x1;

                            return (
                                <source
                                    key={`${size}-${image.urlAndFormat.format}-${index}-${imageIndex}`}
                                    srcSet={`${x2} 2x, ${x1}`}
                                    media={getMinWidthMedia(mediaQueries[size])}
                                    type={`image/${image.urlAndFormat.format}`}
                                />
                            );
                        })}
                </React.Fragment>
            );

            const getWebURL = (ratio: ImageRatios) =>
                getFirstURL(
                    extractWebImages(webSources, 'small', ratio).sort(
                        sortImageFormats([...imageFormatsInOrder].reverse()), // reverse is because in <img> tag we need png or jpg, otherwise it will not work on iOS
                    ),
                );

            // Размеры берутся из варианта small: предполагается, что бэкенд отдаёт
            // все размеры с одинаковым соотношением сторон.
            const imageDimensions = getImageDimensions(
                extractWebImages(webSources, 'small', 'x1').sort(
                    sortImageFormats([...imageFormatsInOrder].reverse()),
                )[0],
            );

            const preview = getPreviewSRC(webSources);

            const getSmallSRC = (ratio: ImageRatios) => getSafeURL(getSRC(getWebURL(ratio)));
            const x1 = getSmallSRC('x1');
            const x2 = getSmallSRC('x2');

            return (
                <picture
                    ref={pictureRef}
                    className={clsx(
                        classes?.picture,
                        isLazy && styles.withSkeleton,
                        isImageLoaded && styles.loaded,
                    )}
                >
                    {isWithLoader && !isImageLoaded && isLazy && !pictureRef.current !== null && (
                        <Spinner classes={{ root: styles.spinner }} size="large" />
                    )}

                    {(priority
                        ? (['large', 'medium', 'small'] as WebImageSizes[])
                        : (['large', 'medium'] as WebImageSizes[])
                    ).map(renderSources)}

                    {!priority &&
                        (preview === null ? (
                            <span className={clsx(otherProps.className, styles.loader)} />
                        ) : (
                            <span
                                className={clsx(otherProps.className, styles.preview)}
                                style={
                                    {
                                        '--preview': `url('${preview}')`,
                                    } as CSSProperties
                                }
                            />
                        ))}

                    <img
                        src={x1}
                        srcSet={`${x2} 2x`}
                        alt={alt ?? ''}
                        ref={currentImageRef}
                        onLoad={() => {
                            onLoadImage();
                            if (onLoad !== undefined) onLoad();
                        }}
                        itemProp={shouldHaveItemProp ? 'image' : undefined}
                        {...imageDimensions}
                        {...priorityAttrs}
                        {...otherProps}
                    />
                </picture>
            );
        } else if (sources.mobile !== undefined) {
            const mobileSources = sources.mobile;

            const renderSources = (size: MobileImageSizes): JSX.Element => (
                <>
                    {extractMobileImages(mobileSources, 'x1').flatMap((image, index) => {
                        return (
                            <source
                                key={`${size}-${image.urlAndFormat.format}-${index}`}
                                srcSet={`${getSafeURL(getSRC(image.urlAndFormat.url))}`}
                                media={getMinWidthMedia(mediaQueries.small)}
                                type={`image/${image.urlAndFormat.format}`}
                            />
                        );
                    })}
                </>
            );

            const getMobileSRC = (ratio: ImageRatios) =>
                getSafeURL(getSRC(getFirstURL(extractMobileImages(mobileSources, ratio))));

            const x1 = getMobileSRC('x1');
            const x2 = getMobileSRC('x2');
            const imageDimensions = getImageDimensions(extractMobileImages(mobileSources, 'x1')[0]);

            return (
                <picture
                    ref={pictureRef}
                    className={clsx(
                        classes?.picture,
                        isLazy && styles.withSkeleton,
                        isImageLoaded && styles.loaded,
                    )}
                >
                    {isWithLoader && !isImageLoaded && isLazy && !pictureRef.current !== null && (
                        <Spinner classes={{ root: styles.spinner }} size="large" />
                    )}

                    {renderSources('single')}

                    {!priority && <span className={clsx(otherProps.className, styles.loader)} />}

                    <img
                        src={x1}
                        srcSet={`${x2} 2x`}
                        alt={alt ?? ''}
                        ref={currentImageRef}
                        onLoad={() => {
                            onLoadImage();
                            if (onLoad !== undefined) onLoad();
                        }}
                        itemProp={shouldHaveItemProp ? 'image' : undefined}
                        {...imageDimensions}
                        {...priorityAttrs}
                        {...otherProps}
                    />
                </picture>
            );
        }

        return (
            <div className={clsx(classes?.picture, styles.noImageWrapper)}>
                <NoImageSquareIcon className={styles.noImageIcon} />
            </div>
        );
    }

    return (
        <picture className={clsx(classes?.picture, isImageLoaded && styles.loaded)}>
            <img
                src={src}
                srcSet={src2x ? `${src2x} 2x` : undefined}
                alt={alt ?? ''}
                ref={currentImageRef}
                onLoad={() => {
                    onLoadImage();
                    if (onLoad !== undefined) onLoad();
                }}
                {...priorityAttrs}
                {...otherProps}
            />
        </picture>
    );
};

export { Picture };
